import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./Table.module.css";

const TableContext = createContext(null);

function toPx(value) {
  if (value == null) return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

function joinClassNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function buildStickyOffsets(widthMap, stickyMap) {
  const offsets = {};
  let runningLeft = 0;

  const stickyIndexes = Object.keys(stickyMap)
    .map(Number)
    .filter((index) => stickyMap[index])
    .sort((a, b) => a - b);

  stickyIndexes.forEach((index) => {
    offsets[index] = runningLeft;
    runningLeft += widthMap[index] || 0;
  });

  return offsets;
}

function getEffectiveWrapOrTruncate(wrap, wrapOrTruncate = "truncate") {
  if (wrap === true) return "wrap";
  return wrapOrTruncate;
}

function getWrapOrTruncateClassName(wrapOrTruncate = "truncate") {
  return wrapOrTruncate === "wrap" ? styles.wrap : styles.truncate;
}

function getWiderClassName(wider) {
  return wider ? styles.wider : "";
}

function getAlignClassName(align) {
  if (align === "right") return styles.alignRight;
  if (align === "center") return styles.alignCenter;
  if (align === "left") return styles.alignLeft;
  return "";
}

export function TableShell({
  children,
  maxHeight,
  style,
  className = "",
  defaultVisibleColumnCount = 4,
}) {
  const shellRef = useRef(null);
  const resizeObserversRef = useRef(new Map());

  const [headerWidths, setHeaderWidths] = useState({});
  const [stickyColumns, setStickyColumns] = useState({});
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    const element = shellRef.current;
    if (!element) return undefined;

    const updateContainerWidth = () => {
      const width = Math.floor(element.getBoundingClientRect().width);
      setContainerWidth((prev) => (prev === width ? prev : width));
    };

    updateContainerWidth();

    const ro = new ResizeObserver(() => {
      updateContainerWidth();
    });

    ro.observe(element);

    return () => {
      ro.disconnect();
    };
  }, []);

  const registerHeaderCell = useCallback((columnIndex, element, isSticky = false) => {
    if (!element || columnIndex == null) {
      return () => {};
    }

    setStickyColumns((prev) => {
      if (prev[columnIndex] === !!isSticky) return prev;
      return { ...prev, [columnIndex]: !!isSticky };
    });

    const updateWidth = () => {
      const measuredWidth = Math.ceil(element.getBoundingClientRect().width);
      setHeaderWidths((prev) => {
        if (prev[columnIndex] === measuredWidth) return prev;
        return { ...prev, [columnIndex]: measuredWidth };
      });
    };

    updateWidth();

    const existing = resizeObserversRef.current.get(columnIndex);
    if (existing) {
      existing.disconnect();
      resizeObserversRef.current.delete(columnIndex);
    }

    const ro = new ResizeObserver(() => {
      updateWidth();
    });

    ro.observe(element);
    resizeObserversRef.current.set(columnIndex, ro);

    return () => {
      const current = resizeObserversRef.current.get(columnIndex);
      if (current) {
        current.disconnect();
        resizeObserversRef.current.delete(columnIndex);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      resizeObserversRef.current.forEach((ro) => ro.disconnect());
      resizeObserversRef.current.clear();
    };
  }, []);

  const defaultColumnWidth = useMemo(() => {
    if (!containerWidth || !defaultVisibleColumnCount) return undefined;
    return Math.floor(containerWidth / defaultVisibleColumnCount);
  }, [containerWidth, defaultVisibleColumnCount]);

  const effectiveWidths = useMemo(() => {
    const merged = { ...headerWidths };

    Object.keys(stickyColumns).forEach((key) => {
      const columnIndex = Number(key);
      if (!merged[columnIndex] && defaultColumnWidth) {
        merged[columnIndex] = defaultColumnWidth;
      }
    });

    return merged;
  }, [headerWidths, stickyColumns, defaultColumnWidth]);

  const offsets = useMemo(
    () => buildStickyOffsets(effectiveWidths, stickyColumns),
    [effectiveWidths, stickyColumns]
  );

  const contextValue = useMemo(
    () => ({
      headerWidths,
      stickyColumns,
      offsets,
      registerHeaderCell,
      defaultColumnWidth,
    }),
    [headerWidths, stickyColumns, offsets, registerHeaderCell, defaultColumnWidth]
  );

  return (
    <TableContext.Provider value={contextValue}>
      <div
        ref={shellRef}
        className={joinClassNames(styles.shell, className)}
        style={{
          maxHeight,
          ...style,
        }}
      >
        {children}
      </div>
    </TableContext.Provider>
  );
}

export const Table = forwardRef(function Table(
  { children, style, className = "" },
  ref
) {
  return (
    <table
      ref={ref}
      className={joinClassNames(styles.table, className)}
      style={style}
    >
      {children}
    </table>
  );
});

function useCellStyles({
  columnIndex,
  sticky,
  stickyOffset,
  width,
  isHeader,
}) {
  const ctx = useContext(TableContext);

  const isStickyColumn = !!sticky;

  const explicitWidth = toPx(width);
  const softMaxWidth = width == null ? toPx(ctx?.defaultColumnWidth) : undefined;

  const computedLeft =
    stickyOffset != null
      ? toPx(stickyOffset)
      : isStickyColumn
      ? `${ctx?.offsets?.[columnIndex] || 0}px`
      : undefined;

  return {
    left: isStickyColumn ? computedLeft : undefined,
    width: explicitWidth,
    minWidth: explicitWidth,
    maxWidth: explicitWidth ?? softMaxWidth,
    zIndex: isHeader ? (isStickyColumn ? 6 : undefined) : isStickyColumn ? 2 : undefined,
  };
}

export const Th = forwardRef(function Th(
  {
    children,
    columnIndex,
    sticky = false,
    stickyOffset,
    wrap = false,
    wider = false,
    wrapOrTruncate = "truncate",
    width,
    align,
    style,
    className = "",
    ...rest
  },
  forwardedRef
) {
  const ctx = useContext(TableContext);
  const localRef = useRef(null);

  useLayoutEffect(() => {
    if (!ctx?.registerHeaderCell) return undefined;
    if (typeof columnIndex !== "number") return undefined;
    return ctx.registerHeaderCell(columnIndex, localRef.current, sticky);
  }, [ctx?.registerHeaderCell, columnIndex, sticky]);

  const cellStyles = useCellStyles({
    columnIndex,
    sticky,
    stickyOffset,
    width,
    isHeader: true,
  });

  const effectiveWrapOrTruncate = getEffectiveWrapOrTruncate(
    wrap,
    wrapOrTruncate
  );

  return (
    <th
      ref={(node) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      }}
      className={joinClassNames(
        styles.th,
        sticky && styles.stickyColumn,
        getWrapOrTruncateClassName(effectiveWrapOrTruncate),
        getWiderClassName(wider),
        getAlignClassName(align),
        className
      )}
      style={{
        ...cellStyles,
        ...(wrap ? { maxWidth: "20vw" } : {}),
        ...(wider ? { width: "auto", maxWidth: "33vw" } : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </th>
  );
});

export const Td = forwardRef(function Td(
  {
    children,
    columnIndex,
    sticky = false,
    stickyOffset,
    wrap = false,
    wider = false,
    wrapOrTruncate = "truncate",
    width,
    align,
    style,
    className = "",
    ...rest
  },
  forwardedRef
) {
  const cellStyles = useCellStyles({
    columnIndex,
    sticky,
    stickyOffset,
    width,
    isHeader: false,
  });

  const effectiveWrapOrTruncate = getEffectiveWrapOrTruncate(
    wrap,
    wrapOrTruncate
  );

  return (
    <td
      ref={forwardedRef}
      className={joinClassNames(
        styles.td,
        sticky && styles.stickyColumn,
        getWrapOrTruncateClassName(effectiveWrapOrTruncate),
        getWiderClassName(wider),
        getAlignClassName(align),
        className
      )}
      style={{
        ...cellStyles,
        ...(wrap ? { maxWidth: "20vw" } : {}),
        ...(wider ? { width: "auto", maxWidth: "33vw" } : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </td>
  );
});

export const NumberTd = forwardRef(function NumberTd(
  { align = "right", className = "", ...props },
  ref
) {
  return (
    <Td
      ref={ref}
      align={align}
      className={joinClassNames(styles.number, className)}
      {...props}
    />
  );
});